import { PageHeaderWrapper } from '@/app/layouts/PageHeaderWrapper'
import { selectBankNameById } from '@/entities/banks'
import {
  PaymentRequisitMobileBlock,
  paymentRequisitsColumns,
  useEnrichedPaymentRequisites,
} from '@/entities/paymentRequisit'

import {
  DialogEditRequisit,
  RequisiteBanner,
  useRequisitPageData,
  useRequisitPageHandlers,
} from '@/features/requisites'
import EditIcon from '@/shared/assets/icons/edit.svg?react'
import PauseIcon from '@/shared/assets/icons/pause.svg?react'
import PlayIcon from '@/shared/assets/icons/play.svg?react'
import FarmIcon from '@/shared/assets/icons/farm.svg?react'
import { BaseButton } from '@/shared/components'
import { DeleteDialog } from '@/shared/components/BaseDialog/DeleteDialog'
import { DeviceBlock } from '@/shared/components/DeviceBlock/DeviceBlock'
import { BankIcon } from '@/shared/components/icons/banks/BankIcon'
import { BasePageSkeleton } from '@/shared/components/Skeletones/BasePageSkeleton'
import { getShortenId } from '@/shared/utils/getShortenId'
import { useAppSelector } from '@/store/hooks'
import { useEffect } from 'react'
import { FarmModel } from '@/entities/farm'
import { farmsAPI } from '@/features/farm'
import { useApiQuery } from '@/api/main/query'
import { CallbackList } from '@/entities/callback/ui/CallbackList.tsx'
import { CopyBlock } from '@/shared/components/CopyBlock/CopyBlock'
import { BaseTable } from 'syncra-storybook/features'
import { SmallBaseTableSkeleton } from '@/features/baseTable/ui/SmallBaseTableSkeleton'
import { formatRequisite } from '@/shared/lib'

export const RequisitPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const {
    id,
    requisitData,
    requisitIsLoad,
    requisitError,
    requisiteStatData,
    requisiteStatIsLoad,
    status,
  } = useRequisitPageData()

  const {
    modalEditRequisitVisible,
    setModalEditRequisitVisible,
    modalDeleteRequisit,
    setModalDeleteRequisit,
    modalDeleteBankcard,
    setModalDeleteBankcard,
    deletedNameRequisit,
    handleDeleteRequisit,
    handleUpdateRequisitSubmit,
    handleEnableRequisit,
    handleDisableRequisit,
    handleOpenModal,
    handleDisableBankcard,
    handleEnableBankcard,
    handleOpenDeleteDialog,
    handleDeleteBankcard,
  } = useRequisitPageHandlers(id, requisitData)

  const {
    enriched: enrichedPaymentRequisites,
    isLoading: isStatsLoading,
    isError: isStatsError,
  } = useEnrichedPaymentRequisites(requisitData?.paymentRequisitProfiles)

  const { data: queryFarmData } = useApiQuery<FarmModel>(
    ['farm', id],
    farmsAPI.getFarm(requisitData?.farmId ?? ''),
    undefined,
    { enabled: !!requisitData?.farmId }
  )

  const subtitleClasses = 'flex items-center gap-2 text-text-secondary text-sm leading-[18px]'

  const fullName = `${requisitData?.personSurname ?? ''} ${requisitData?.personName ?? ''} ${requisitData?.personMiddlename ?? ''}`
  const bankName = useAppSelector((state) => selectBankNameById(state, requisitData?.bankId ?? ''))

  const paymentRequisit = formatRequisite(requisitData?.paymentRequisitProfiles?.[0].value ?? '')

  if (requisitError) return <p>Ошибка загрузки данных.</p>
  if (requisitIsLoad || requisiteStatIsLoad) return <BasePageSkeleton />
  if (!requisitData) return null

  return (
    <div className="flex flex-col gap-6 md:gap-10">
      <PageHeaderWrapper
        borderTitle
        icon={
          <div className="flex items-center gap-4">
            <BankIcon bankId={requisitData.bankId} status={status} className="w-[52px] h-[52px]" />
            <h4 className="md:hidden font-klein font-medium text-text-secondary text-lg">
              {bankName}
            </h4>
          </div>
        }
        title={fullName}
        subtitle={
          <div className="flex md:flex-row flex-col gap-2">
            <span className={subtitleClasses}>{paymentRequisit}</span>

            <CopyBlock text={requisitData.id} color="secondary" title={requisitData.id}>
              ID: {getShortenId(requisitData.id)}
            </CopyBlock>

            {requisitData.farmId && queryFarmData && (
              <div className="flex items-center gap-1">
                Прикреплен к:
                <div className="flex items-center gap-1 text-primary">
                  <FarmIcon />
                  <span>{queryFarmData.name ?? ''}</span>
                </div>
              </div>
            )}
          </div>
        }
        actions={
          <div className="flex items-center gap-4 w-full">
            <BaseButton
              variant="setting"
              padding="none"
              radius="md"
              className="py-3 w-full md:w-[225px]"
              iconPosition="right"
              icon={<EditIcon className="text-primary dark:text-primary-dark" />}
              onClick={handleOpenModal}
            >
              Редактировать
            </BaseButton>
            <BaseButton
              variant="primary"
              padding="none"
              radius="md"
              className="w-full md:w-[225px]"
              iconPosition="right"
              icon={
                requisitData.isDisabledByUser ? (
                  <PlayIcon className="text-white" />
                ) : (
                  <PauseIcon className="text-white" />
                )
              }
              onClick={requisitData.isDisabledByUser ? handleEnableRequisit : handleDisableRequisit}
            >
              {requisitData.isDisabledByUser ? 'Запустить' : 'Остановить'}
            </BaseButton>
          </div>
        }
      />

      <RequisiteBanner
        detail={requisitData}
        statistic={requisiteStatData?.items}
      />

      {isStatsError ? (
        <p>Ошибка загрузки данных</p>
      ) : isStatsLoading ? (
        <SmallBaseTableSkeleton />
      ) : (
        <BaseTable
          title="Счета/Карты"
          columns={paymentRequisitsColumns({
            onDisable: handleDisableBankcard,
            onEnable: handleEnableBankcard,
            onDelete: handleOpenDeleteDialog,
            maxDailyLimit: requisitData.maxDailyLimit ?? 0,
            requesit: requisitData
          })}
          data={enrichedPaymentRequisites}
          cardPadding="sm"
          cardVariant="secondary"
          cardClasses="flex flex-col gap-6"
          mobileCards={enrichedPaymentRequisites?.map((requisite) => (
            <PaymentRequisitMobileBlock
              key={requisite.id}
              paymentRequisit={requisite}
              maxDailyLimit={requisitData.maxDailyLimit ?? 0}
              requesit={requisitData}
            />
          ))}
        />
      )}

      <DeviceBlock id={requisitData.deviceId} />
      <CallbackList requisitId={id} />
      <DeleteDialog
        nameDeletedItem={deletedNameRequisit ? `реквизит ${deletedNameRequisit}` : 'реквизит'}
        modalVisible={modalDeleteBankcard}
        setModalVisible={setModalDeleteBankcard}
        onSubmit={handleDeleteBankcard}
      />

      <DeleteDialog
        nameDeletedItem="реквизит"
        modalVisible={modalDeleteRequisit}
        setModalVisible={setModalDeleteRequisit}
        onSubmit={handleDeleteRequisit}
      />

      {modalEditRequisitVisible && (
        <DialogEditRequisit
          modalVisible={modalEditRequisitVisible}
          setModalVisible={setModalEditRequisitVisible}
          onSubmit={handleUpdateRequisitSubmit}
          onDelete={() => setModalDeleteRequisit(true)}
          onDeleteOptionalCard={handleOpenDeleteDialog}
          requisitData={requisitData}
        />
      )}
    </div>
  )
}
